import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CRMUser } from '../types';
import type { ScenarioLine } from '../utils/cashFlowScenario';
import {
  canDecideCapitalPlan,
  canTransitionCapitalPlanStatus,
  extractMentionUserIds,
  isCapitalPlanMentionableUser,
  listCapitalPlanNotifyUids,
  type CapitalPlan,
  type CapitalPlanComment,
  type CapitalPlanStatus,
} from '../utils/capitalPlans';
import {
  addCapitalPlanComment,
  createCapitalPlan,
  deleteCapitalPlan,
  notifyCapitalPlanEvent,
  subscribeCapitalPlanComments,
  subscribeCapitalPlans,
  updateCapitalPlan,
  updateCapitalPlanStatus,
} from '../utils/capitalPlanService';
import type { EmployeeDesignation, ExecutiveBriefingProfile } from '../types';

export function useCapitalPlanWorkspace(args: {
  actorUserId: string | undefined;
  actorName: string;
  authRole: string | undefined;
  designations: EmployeeDesignation[] | string[] | undefined;
  briefingProfile?: ExecutiveBriefingProfile | null;
  crmUsers: CRMUser[];
  initialPlanId?: string | null;
}) {
  const [plans, setPlans] = useState<CapitalPlan[]>([]);
  const [activePlanId, setActivePlanId] = useState<string | null>(args.initialPlanId ?? null);
  const [comments, setComments] = useState<CapitalPlanComment[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return subscribeCapitalPlans(setPlans, (err) => setError(err.message));
  }, []);

  useEffect(() => {
    if (args.initialPlanId) setActivePlanId(args.initialPlanId);
  }, [args.initialPlanId]);

  useEffect(() => {
    if (!activePlanId) {
      setComments([]);
      return undefined;
    }
    return subscribeCapitalPlanComments(activePlanId, setComments, (err) => setError(err.message));
  }, [activePlanId]);

  const activePlan = useMemo(
    () => plans.find((p) => p.id === activePlanId) ?? null,
    [plans, activePlanId],
  );

  const canDecide = canDecideCapitalPlan({
    authRole: args.authRole,
    designations: args.designations,
    briefingProfile: args.briefingProfile,
  });

  const mentionableUsers = useMemo(
    () => args.crmUsers.filter((u) => isCapitalPlanMentionableUser(u)),
    [args.crmUsers],
  );

  const mentionCandidates = useMemo(
    () => mentionableUsers.map((u) => ({ uid: u.uid, displayName: u.displayName || u.email })),
    [mentionableUsers],
  );

  const save = useCallback(async (input: {
    title: string;
    why: string;
    lines: ScenarioLine[];
    share?: boolean;
    quiet?: boolean;
  }) => {
    if (!args.actorUserId) {
      setError('Sign in to save this plan.');
      return null;
    }
    if (!input.quiet) setBusy(true);
    setError(null);
    try {
      if (activePlanId) {
        await updateCapitalPlan({
          planId: activePlanId,
          title: input.title,
          why: input.why,
          lines: input.lines,
          actorUserId: args.actorUserId,
          actorName: args.actorName,
        });
        if (
          input.share
          && activePlan
          && activePlan.status !== 'shared'
          && canTransitionCapitalPlanStatus(activePlan.status, 'shared', { canDecide })
        ) {
          await updateCapitalPlanStatus({
            planId: activePlanId,
            status: 'shared',
            actorUserId: args.actorUserId,
            actorName: args.actorName,
          });
          await notifyCapitalPlanEvent({
            userIds: listCapitalPlanNotifyUids(args.crmUsers, args.actorUserId),
            title: 'Capital plan shared',
            message: `${args.actorName} shared “${input.title.trim() || 'Untitled plan'}”`,
            planId: activePlanId,
            actorUserId: args.actorUserId,
          });
        }
        return activePlanId;
      }
      const id = await createCapitalPlan({
        title: input.title,
        why: input.why,
        lines: input.lines,
        actorUserId: args.actorUserId,
        actorName: args.actorName,
        share: input.share,
      });
      setActivePlanId(id);
      if (input.share) {
        await notifyCapitalPlanEvent({
          userIds: listCapitalPlanNotifyUids(args.crmUsers, args.actorUserId),
          title: 'Capital plan shared',
          message: `${args.actorName} shared “${input.title.trim() || 'Untitled plan'}”`,
          planId: id,
          actorUserId: args.actorUserId,
        });
      }
      return id;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save plan');
      return null;
    } finally {
      if (!input.quiet) setBusy(false);
    }
  }, [activePlan, activePlanId, args.actorName, args.actorUserId, args.crmUsers, canDecide]);

  const setStatus = useCallback(async (planId: string, status: CapitalPlanStatus) => {
    const plan = plans.find((p) => p.id === planId);
    if (!args.actorUserId || !plan) return;
    if (plan.status === status) return;
    if (!canTransitionCapitalPlanStatus(plan.status, status, { canDecide })) {
      setError('You cannot set that status.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await updateCapitalPlanStatus({
        planId,
        status,
        actorUserId: args.actorUserId,
        actorName: args.actorName,
      });
      await notifyCapitalPlanEvent({
        userIds: [
          ...listCapitalPlanNotifyUids(args.crmUsers, args.actorUserId),
          plan.createdByUserId,
        ],
        title: 'Capital plan updated',
        message: `${args.actorName} marked “${plan.title}” ${status.replace('_', ' ')}`,
        planId,
        actorUserId: args.actorUserId,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update status');
    } finally {
      setBusy(false);
    }
  }, [args.actorName, args.actorUserId, args.crmUsers, canDecide, plans]);

  const postComment = useCallback(async (input: {
    body: string;
    bodyHtml?: string;
    mentionUserIds?: string[];
  }) => {
    if (!args.actorUserId || !activePlanId || !activePlan) return;
    const body = input.body.trim();
    if (!body) return;
    setBusy(true);
    setError(null);
    try {
      await addCapitalPlanComment({
        planId: activePlanId,
        body,
        bodyHtml: input.bodyHtml,
        mentionUserIds: input.mentionUserIds,
        actorUserId: args.actorUserId,
        actorName: args.actorName,
        mentionCandidates,
      });
      const mentionIds = input.mentionUserIds?.length
        ? input.mentionUserIds
        : extractMentionUserIds(body, mentionCandidates);
      await notifyCapitalPlanEvent({
        userIds: [...mentionIds, activePlan.createdByUserId],
        title: mentionIds.length ? 'You were mentioned on a capital plan' : 'New comment on a capital plan',
        message: `${args.actorName} on “${activePlan.title}”: ${body.slice(0, 120)}`,
        planId: activePlanId,
        actorUserId: args.actorUserId,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to post comment');
    } finally {
      setBusy(false);
    }
  }, [activePlan, activePlanId, args.actorName, args.actorUserId, mentionCandidates]);

  const startNew = useCallback(() => {
    setActivePlanId(null);
  }, []);

  const remove = useCallback(async (planId: string) => {
    setBusy(true);
    setError(null);
    try {
      await deleteCapitalPlan(planId);
      if (activePlanId === planId) setActivePlanId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to delete plan');
      return false;
    } finally {
      setBusy(false);
    }
    return true;
  }, [activePlanId]);

  return {
    plans,
    activePlan,
    activePlanId,
    setActivePlanId,
    startNew,
    comments,
    busy,
    error,
    canDecide,
    mentionCandidates,
    mentionableUsers,
    save,
    setStatus,
    postComment,
    remove,
  };
}
